import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  Fade,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  Palette,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import SeasonalThemeSelector from '../ui/SeasonalThemeSelector';

// Navigation links
const navLinks = [
  { title: 'Home', path: '/' },
  { title: 'Experience', path: '/experience' },
  { title: 'Projects', path: '/projects' },
  { title: 'Contact', path: '/contact' },
];

// Get page title based on current path
const getPageTitle = (pathname: string): string => {
  // Home page should always show Portfolio
  if (pathname === '/') {
    return 'Portfolio';
  }
  const route = navLinks.find(link => link.path === pathname);
  return route ? route.title : 'Portfolio';
};

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showPageTitle, setShowPageTitle] = useState(false);
  const [currentPageTitle, setCurrentPageTitle] = useState('');
  const [titleKey, setTitleKey] = useState(0); // For forcing re-render of Fade component
  const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null);

  // Change navbar style on scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
        
        // Update page title with delay for smooth transition
        if (isScrolled) {
          setTimeout(() => {
            const newTitle = getPageTitle(location.pathname);
            setCurrentPageTitle(newTitle);
            setShowPageTitle(true);
          }, 150); // Small delay to allow background transition
        } else {
          setShowPageTitle(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled, location.pathname]);

  // Update title when route changes
  useEffect(() => {
    const newTitle = getPageTitle(location.pathname);
    if (scrolled && newTitle !== currentPageTitle) {
      setShowPageTitle(false); // Fade out current title
      setTimeout(() => {
        setCurrentPageTitle(newTitle);
        setShowPageTitle(true); // Fade in new title
        setTitleKey(prev => prev + 1); // Force re-render for smooth transition
      }, 200);
    } else if (!scrolled) {
      setCurrentPageTitle(newTitle);
    }
  }, [location.pathname, scrolled, currentPageTitle]);

  // Handle drawer toggle
  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  // Handle theme selector popover
  const handleThemeClick = (event: React.MouseEvent<HTMLElement>) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleThemeClose = () => {
    setThemeAnchorEl(null);
  };

  const themePopoverOpen = Boolean(themeAnchorEl);

  return (
    <>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: scrolled ? theme.palette.background.paper : 'transparent',
            opacity: 1,
            transition: 'all 0.3s',
            backdropFilter: scrolled ? 'blur(10px)' : 'none',
            borderBottom: scrolled
              ? `1px solid ${theme.palette.divider}`
              : 'none',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Logo / Dynamic Title */}
            <Box sx={{ position: 'relative', height: '1.5rem', display: 'flex', alignItems: 'center' }}>
              {/* Default PORTFOLIO title */}
              <Fade in={!showPageTitle} timeout={300}>
                <Typography
                  variant="h6"
                  component={Link}
                  to="/"
                  sx={{
                    textDecoration: 'none',
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    letterSpacing: 1,
                    position: showPageTitle ? 'absolute' : 'static',
                    opacity: showPageTitle ? 0 : 1,
                  }}
                >
                  PORTFOLIO
                </Typography>
              </Fade>
              
              {/* Dynamic page title */}
              <Fade in={showPageTitle} timeout={300} key={titleKey}>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    letterSpacing: 1,
                    position: !showPageTitle ? 'absolute' : 'static',
                    opacity: !showPageTitle ? 0 : 1,
                  }}
                >
                  {currentPageTitle.toUpperCase()}
                </Typography>
              </Fade>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {navLinks.map(link => (
                  <Button
                    key={link.title}
                    component={Link}
                    to={link.path}
                    sx={{
                      mx: 1,
                      opacity: location.pathname === link.path ? 1 : 0.7,
                      fontWeight: location.pathname === link.path ? 600 : 400,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: location.pathname === link.path ? '50%' : '0%',
                        height: '2px',
                        bottom: 0,
                        left: '25%',
                        bgcolor: 'primary.main',
                        transition: 'all 0.3s',
                      },
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'transparent',
                        '&::after': {
                          width: '50%',
                        },
                      },
                    }}
                  >
                    {link.title}
                  </Button>
                ))}
                <IconButton 
                  onClick={handleThemeClick} 
                  sx={{ ml: 2 }}
                  aria-label="Theme settings"
                  color="inherit"
                >
                  <Palette sx={{ color: theme.palette.text.primary }} />
                </IconButton>
              </Box>
            )}

            {/* Mobile Navigation Icon */}
            {isMobile && (
              <Box sx={{ display: 'flex' }}>
                <IconButton 
                  onClick={handleThemeClick} 
                  color="inherit"
                  aria-label="Theme settings"
                >
                  <Palette sx={{ color: theme.palette.text.primary }} />
                </IconButton>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                >
                  <MenuIcon sx={{ color: theme.palette.text.primary }} />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '70%',
            maxWidth: 300,
            bgcolor: 'primary',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={toggleDrawer(false)}>
            <Close />
          </IconButton>
        </Box>
        <List>
          {navLinks.map(link => (
            <ListItem key={link.title} disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                onClick={toggleDrawer(false)}
                sx={{
                  py: 2,
                  bgcolor:
                    location.pathname === link.path
                      ? theme.palette.action.selected
                      : 'transparent',
                }}
              >
                <ListItemText
                  primary={link.title}
                  slotProps={{
                    primary: {
                      fontWeight: location.pathname === link.path ? 600 : 400,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Theme Selector Popover */}
      <Popover
        open={themePopoverOpen}
        anchorEl={themeAnchorEl}
        onClose={handleThemeClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
        }}
      >
        <Box sx={{ p: 3, minWidth: 280 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Theme Mode
            </Typography>
          </Box>
          <SeasonalThemeSelector />
        </Box>
      </Popover>

      {/* Toolbar spacer */}
      <Toolbar />
    </>
  );
};

export default Navbar;
